/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { di } from 'react-magnetic-di';

import { cssMap, jsx } from '@atlaskit/css';
import Lozenge from '@atlaskit/lozenge';
import { Pressable } from '@atlaskit/primitives/compiled';
import { HoverCard } from '@atlaskit/smart-card/hover-card';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type Source } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { extractProductNameFromARI } from '../../../utils/ari';
import { transformIfConfluenceLink } from '../../../utils/url-transformer';
import { useMessageSources } from '../message-source-context';

const styles = cssMap({
	citation: {
		border: 'none',
		backgroundColor: 'transparent',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		'&:hover': {
			cursor: 'pointer',
		},
	},
});

const convertColorAppearance = (color: string) => {
	switch (color) {
		case 'blue':
			return 'information';
		case 'green':
			return 'success';
		case 'red':
			return 'danger';
		case 'yellow':
			return 'warning';
		case 'purple':
			return 'discovery';
		default:
			return 'neutral';
	}
};

type StatusCitationProps = {
	localId: string;
	text: string;
	color?: string;
	onClick?: (index: number, citedProduct: string | null, product: string | null) => void;
};

// Handles citation clicks and conversion of `status` nodes
// that are not citations into lozenges to preserve future usage
export const StatusCitation = ({
	localId,
	text,
	color = 'blue',
	onClick,
}: StatusCitationProps): JSX.Element => {
	di(useMessageSources, HoverCard);
	const { sendAnalyticsEvent } = useAnalytics();
	const { sources } = useMessageSources();
	const { source, index } = React.useMemo(() => {
		if (!localId.includes('citation')) {
			return {};
		}

		const index = parseInt(localId.split('-')?.[1]);

		return {
			source: sources?.[index - 1],
			index,
		};
	}, [localId, sources]);

	const handleClick = React.useCallback(
		(source: Source, transformedUrl: string) => {
			const citedProduct = extractProductNameFromARI(source.ari);
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'citationLink',
				attributes: {
					index,
					sourceType: source.type,
					citedProduct,
					sourceProduct: source.product,
				},
			});

			onClick?.(index ?? 0, citedProduct, source.product ?? null);
			window.open(transformedUrl, '_blank', 'noopener, noreferrer');
		},
		[index, sendAnalyticsEvent, onClick],
	);

	// Our special citation treatment
	if (!!source) {
		const transformedUrl = source.url ? transformIfConfluenceLink(source.url, true) : source.url;
		return (
			<HoverCard url={transformedUrl}>
				<Pressable
					xcss={styles.citation}
					onClick={() => {
						handleClick(source, transformedUrl);
					}}
				>
					<Lozenge appearance="information">{index}</Lozenge>
				</Pressable>
			</HoverCard>
		);
	}

	// Standard lozenge for status nodes
	return <Lozenge appearance={convertColorAppearance(color)}>{text}</Lozenge>;
};
